import React, { useState, useEffect } from "react";
import {Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, Grid, IconButton, InputLabel, makeStyles, MenuItem, Select, Table, TableBody, TableContainer, TableHead, TableRow, TextField, Tooltip} from "@material-ui/core";
import axios from "axios";
import config from "../config";
import { AccountInfo, MemberInfo } from "../types/UserInfoTypes";
import CloseIcon from '@material-ui/icons/CloseOutlined';
import ListIcon from '@material-ui/icons/ListOutlined';
import GroupIcon from '@material-ui/icons/GroupOutlined';
import DeleteIcon from '@material-ui/icons/Delete';
import { Roles } from "./Roles";
import { Groups } from "./Groups";
import { Users } from "./Users";
import { isSuperUser } from "../actions/FlowableTaskActions";


type Group = {
    id: string;
    name: string;
    type: null|string;
}

const useStyles = makeStyles({
    stickyHeader: {
      position: "sticky",
      left: 0,
      top:0,
      backgroundColor: "white",
    }
  });

export function AdminUsers(props:{accountInfo: AccountInfo}):JSX.Element{
    const [loading, setLoading] = useState<boolean>(false);
    const [groups, setGroups] = useState<Group[]>([]);
    const [filterText, setFilterText] = useState('');
    const [selectedGroup, setSelectedGroup] = useState<string>('');
    const [groupMembers, setGroupMembers] = useState<MemberInfo[]>([]);
    const classes = useStyles();

    if (!isSuperUser(props.accountInfo)) {
            return <div>Invalid Permissions to Access this Dashboard</div>;
        }

    useEffect(() => {
        getGroupMembers();
    },[]);

    useEffect(() => {
        if (selectedGroup) {
            setLoading(true);
            const groupNames = [selectedGroup];
            requestAll(`${config.flowableUiApiBaseUrl}idm-app/rest/groups`, {groupNames}).then((response) => {
                if(response.length > 0){
                    setLoading(false);
                    setGroupMembers(response);
                }
                else {
                    setGroupMembers([]);
                }
            })
        }
    }, [selectedGroup]);

    async function getGroupMembers(){
        setLoading(true);
        await axios.get(`${config.flowableUiApiBaseUrl}app/rest/workflow-groups?filter=*`, { withCredentials: true }).then((response) => {
            const groups = response.data.data
            setGroups(groups);
            setLoading(false);
        })
    }

    async function updateMembership(groupName: string, userId: string, updateType: string){
        setLoading(true);
        
        await axios.post(`${config.flowableUiApiBaseUrl}idm-app/rest/user/membership`, {groupName, userId, updateType}, {withCredentials: true}).then(() => {
            const lastGroup = selectedGroup;
            setSelectedGroup('')
            setSelectedGroup(lastGroup);
            setLoading(false);
        }
    )}
/* eslint-disable  @typescript-eslint/no-explicit-any, @typescript-eslint/explicit-module-boundary-types  */
    async function requestAll(url: string,  options: any): Promise<MemberInfo[]> {
        //default amount of members returned, 50
        const size = 50;
        let page = 0;
        // there will only ever be one
      
        const initialResponse = await axios.post(url,
            {...{page, size}, ...options},
            { withCredentials: true });
        if (!initialResponse || !initialResponse.data) {
          return [];
        }
        const members = initialResponse.data[0] ? initialResponse.data[0]: {};
        /* eslint-disable  @typescript-eslint/no-explicit-any */
        let results: any[] = members.members;
        while (results.length < members.totalMembers) {
            page+= size;

          const response = await axios.post(url,
              {...{page, size}, ...options},
              { withCredentials: true });
      
          if (!response || !response.data) {
            break;
          }
          const membersMore = response.data[0] ? response.data[0] : {};
      
          results = [...results, ...membersMore.members];
      
        }
      
        return results;
      }

    const renderMembers = () => {

        const filteredGroupMembers = groupMembers.filter((member) => {
            return  (filterText ? member.username.toLowerCase().includes(filterText) : true);
        })

        if (loading) {
            return <CircularProgress id={'usersLoader'} />;
        }

        if (!loading && filteredGroupMembers.length === 0) {
            return <div style={{marginTop: 20, marginBottom: 20}}>No matching users for the selected group.</div>
        }
    
      return (
        <TableContainer style={{marginTop: 30, marginBottom: 30}}>
            <Table stickyHeader
            aria-label="simple table"
            style={{ tableLayout: "fixed" }}>
                <TableHead>
                    <TableRow>
                        <th align="left" style={{ width: '280px', maxWidth: '280px', paddingLeft: '5px' }} className={classes.stickyHeader}>Username</th>
                        <th align="left" style={{ width: '520px', maxWidth: '520px', paddingLeft: '10px'}} className={classes.stickyHeader}>Email</th>
                        <th align="left" style={{ paddingLeft: '10px' }} className={classes.stickyHeader}>First Name</th>
                        <th align="left" style={{ paddingLeft: '10px' }} className={classes.stickyHeader}>Last Name</th>
                        <th align="left" style={{ paddingLeft: '10px' }} className={classes.stickyHeader}>Actions</th>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {filteredGroupMembers.map((member) => (
                        <TableRow key={member.id} hover={true}>
                            <td style={{ paddingLeft: '10px' }}>{member.username}</td>
                            <td style={{ paddingLeft: '10px'}}>{member.email}</td>
                            <td style={{ paddingLeft: '10px'}}>{member.firstName}</td>
                            <td style={{ paddingLeft: '10px'}}>{member.lastName}</td>
                            <td style={{ paddingLeft: '10px'}}><ViewGroupsButton member={member}/><ViewRolesButton member={member}/><RemoveUserFromGroup member={member}/></td>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
      );
    };

    function ViewGroupsButton(props: {member: MemberInfo}): JSX.Element{
        const [open, setOpen] = useState<boolean>(false)
        return <>
          <Dialog
      open={open}
      onClose={()=>setOpen(false)}
  >
    <DialogTitle>
      User Groups
      <IconButton style={{float:"right"}} onClick={()=>setOpen(false)}>
        <CloseIcon />
      </IconButton>
    </DialogTitle>
    <DialogContent>
        <div style={{fontWeight:"bold", paddingBottom: "10px", fontSize: "18px"}}>{props.member.username}</div>
        {open ? <Groups member={props.member}/> : <></>}
      </DialogContent>
  </Dialog>
  <Tooltip title={"View groups " + props.member.username}>
      <IconButton style={{padding:"3px"}} onClick={e => {
        setOpen(true)
        e.stopPropagation()
      }}>
        <GroupIcon/>
      </IconButton>
    </Tooltip></>
    }

    function ViewRolesButton(props: {member: MemberInfo}): JSX.Element{
        const [open, setOpen] = useState<boolean>(false)
        return <>
          <Dialog
      open={open}
      onClose={()=>setOpen(false)}
  >
    <DialogTitle>
      User Roles
      <IconButton style={{float:"right"}} onClick={()=>setOpen(false)}>
        <CloseIcon />
      </IconButton>
    </DialogTitle>
    <DialogContent>
        <div style={{fontWeight:"bold", paddingBottom: "10px", fontSize: "18px"}}>{props.member.username}</div>
        {open ? <Roles member={props.member}/> : <></>}
      </DialogContent>
  </Dialog>
  <Tooltip title={"View roles " + props.member.username}>
      <IconButton style={{padding:"3px"}} onClick={e => {
        setOpen(true)
        e.stopPropagation()
      }}>
        <ListIcon/>
      </IconButton>
    </Tooltip></>
    }

    function RemoveUserFromGroup(props: {member: MemberInfo}): JSX.Element{
        const [open, setOpen] = useState<boolean>(false)
        return <>
          <Dialog
      open={open}
      onClose={()=>setOpen(false)}
  >
    <DialogTitle>
      Remove User
      <IconButton style={{float:"right"}} onClick={()=>setOpen(false)}>
        <CloseIcon />
      </IconButton>
    </DialogTitle>
    <DialogContent>
        {open ? "Are you sure you want to remove " + props.member.username + " from this group?" : <></>}
    </DialogContent>
        <DialogActions>
            <Button variant="contained" onClick={()=>setOpen(false)} style={{marginRight:"7px"}}>
              Cancel
            </Button>
            <Button color="primary" variant="contained"
                    onClick={
                        () => {
                                updateMembership(selectedGroup, props.member.id, 'leave');
                        }
                    }>
                        Remove 
            </Button>

                    </DialogActions>
  </Dialog>
    <Tooltip title={"Remove User " + props.member.username + " from group"}>
      <IconButton style={{padding:"3px"}} onClick={e => {
        setOpen(true)
        e.stopPropagation()
      }}>
        <DeleteIcon/>
      </IconButton>
    </Tooltip></>
      }
    

    return <>
        <div style={{width:"100%", height: "100%"}}>
            <div style={{backgroundColor:"white", width: "100%", height: "100%", marginTop:40 }} >
                <h1 style={{paddingLeft:"30px", paddingTop:'15px'}}>User Admin</h1>
                <div style={{backgroundColor:"white", height: "100%", width:"95%", margin:"30px"}}>
                    <div  style={{ flexGrow: 1, height: "100%"}}>
                        <Grid container spacing={3}>
                        <Grid item xs={3}>
                                <FormControl style={{width: '100%'}}>
                                    <InputLabel id="process">Select Group</InputLabel>
                                    <Select
                                        labelId="group"
                                        id="group"
                                        value={selectedGroup ?? ''}
                                        onChange={(e) => {
                                            setSelectedGroup(e.target.value  as string);               
                                        }}
                                    >
                                        <MenuItem key={`group-none`} value={''}>Choose Group</MenuItem>
                                        {groups.map((group) => {
                                            return (
                                                <MenuItem key={`group-${group.id}`} value={group.id}>{group.name}</MenuItem>
                                            );
                                        })}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={3}>
                                <FormControl style={{width: '100%'}}>
                                    <TextField
                                        id="filter"
                                        label="Filter by Username"
                                        value={filterText}
                                        onChange={e => setFilterText(e.target.value)}
                                    />
                                </FormControl>
                            </Grid>
                            
                            <Grid item xs={3}>
                                <FormControl style={{width: '100%'}}>
                                    {selectedGroup ? <Users groupName={selectedGroup} groupMembers={groupMembers} onChange={() => {
                                        const lastGroup = selectedGroup;
                                        setSelectedGroup('')
                                        setSelectedGroup(lastGroup);
                                    }} /> : <></>}
                                </FormControl>
                            </Grid>
                        </Grid>
                        <div>{renderMembers()}</div>
                    </div>
                </div>
            </div>
        </div>
        </>;
}